import { z } from 'zod';
import { IsoDateTimeSchema, MediaScaleModeSchema } from './common.js';
import { DeviceCommandStatusSchema } from './device.js';
import { ScreenOrientationSchema, ScreenRotationDegreesSchema } from './screen-rotation.js';
export const CreateScreenRequestSchema = z.object({
    locationId: z.string().min(1),
    name: z.string().min(1).max(200),
    /**
     * Preferred create field. When omitted, legacy `orientation` maps to 0 / 90.
     */
    rotationDegrees: ScreenRotationDegreesSchema.optional(),
    /** @deprecated Prefer rotationDegrees. Kept for older clients. */
    orientation: ScreenOrientationSchema.optional(),
    scaleMode: MediaScaleModeSchema.optional(),
});
export const UpdateScreenRequestSchema = z
    .object({
    scaleMode: MediaScaleModeSchema.optional(),
    rotationDegrees: ScreenRotationDegreesSchema.optional(),
})
    .refine((body) => body.scaleMode !== undefined || body.rotationDegrees !== undefined, { message: 'At least one of scaleMode or rotationDegrees is required' });
export const ScreenPlaybackRefSchema = z.object({
    mediaAssetId: z.string().nullable(),
    /** The asset's original file name; null if the asset was deleted. */
    mediaAssetName: z.string().nullable(),
    playedAt: IsoDateTimeSchema,
});
export const ScreenPlaybackFailureSchema = ScreenPlaybackRefSchema.extend({
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
export const ScreenSchema = z.object({
    id: z.string().min(1),
    organisationId: z.string().min(1),
    locationId: z.string().min(1),
    name: z.string().min(1),
    /** Derived label from rotationDegrees (0/180 landscape, 90/270 portrait). */
    orientation: ScreenOrientationSchema,
    rotationDegrees: ScreenRotationDegreesSchema,
    scaleMode: MediaScaleModeSchema,
    createdAt: IsoDateTimeSchema,
    deviceId: z.string().nullable(),
    lastHeartbeatAt: IsoDateTimeSchema.nullable(),
    reportedPlaylistVersion: z.number().int().nullable(),
    desiredPlaylistVersion: z.number().int().nullable(),
    /**
     * 04: what the screen should play now (same function as /sync) and what it reports.
     * Optional so a dashboard deployed before the API still parses older responses.
     */
    desiredPlaylistId: z.string().nullable().optional(),
    desiredWindowId: z.string().nullable().optional(),
    reportedPlaylistId: z.string().nullable().optional(),
    reportedWindowId: z.string().nullable().optional(),
    /** Last successful sync (not the heartbeat). */
    lastSyncAt: IsoDateTimeSchema.nullable().optional(),
    /** unknown = the player doesn't report this; never shown as behind. */
    syncState: z.enum(['in_sync', 'behind', 'unknown', 'no_content']).optional(),
    syncBehindReasons: z.array(z.enum(['playlist', 'version', 'window', 'schedule'])).optional(),
    /**
     * Proof-of-play (03): last item the screen reported starting, and last failure.
     * Optional so a dashboard deployed before the API still parses older responses.
     */
    lastPlayed: ScreenPlaybackRefSchema.nullable().optional(),
    lastFailure: ScreenPlaybackFailureSchema.nullable().optional(),
});
export const AssignPlaylistRequestSchema = z.object({
    playlistId: z.string().min(1),
});
export const ScreenAssignmentSchema = z.object({
    screenId: z.string().min(1),
    playlistId: z.string().min(1),
    playlistName: z.string().min(1),
    /** `direct` when this screen is playing a one-off content assignment. */
    playlistKind: z.enum(['standard', 'direct']),
});
/** Put one ready media asset on a screen (creates/updates a direct playlist under the hood). */
export const PlayContentOnScreenRequestSchema = z.object({
    mediaAssetId: z.string().min(1),
    durationSeconds: z.number().positive().max(300).optional(),
});
export const PlayContentOnScreenResponseSchema = z.object({
    screenId: z.string().min(1),
    playlistId: z.string().min(1),
    playlistName: z.string().min(1),
    mediaAssetId: z.string().min(1),
    version: z.number().int().positive(),
});
/**
 * Operator-enqueueable remote commands only.
 * Wire protocol may still carry reserved types (screenshot / logs) for old
 * queued rows; those must not be creatable from the dashboard/API.
 */
export const OperatorDeviceCommandTypeSchema = z.enum([
    'SYNC_NOW',
    'RESTART_PLAYER',
    'CLEAR_CACHE',
]);
export const EnqueueDeviceCommandRequestSchema = z.object({
    type: OperatorDeviceCommandTypeSchema,
    payload: z.record(z.unknown()).optional(),
});
/**
 * Idempotency-Key header value. UUID only -- bounded, opaque, carries no
 * secrets/user data/command contents. Transport is header-only; this is
 * not part of EnqueueDeviceCommandRequestSchema (the JSON body).
 */
export const IdempotencyKeySchema = z.string().uuid();
/**
 * Dedicated response for POST /devices/:deviceId/commands. Kept separate
 * from DeviceCommandStatusSchema (the ordinary status-read response) so
 * `created` never appears on a plain status read and is never confused
 * with `createdAt`.
 */
export const EnqueueDeviceCommandResponseSchema = DeviceCommandStatusSchema.extend({
    /** true if this call created the command; false if it returned an existing idempotent match. */
    created: z.boolean(),
});
//# sourceMappingURL=screen.js.map